import { CalendarEvent } from 'interfaces/calendar.interface';
import { RoleTaskFull } from 'interfaces/roleTask.interface';
import { TaskUserUtils } from 'utils/taskUser.utils';
import { dayjs, Equipment, ShortUser } from '@umanforge/common';
import { ErrorIcons } from 'components/other/_ErrorIcons';
import { useEffect, useState } from 'react';
import { RecurrenceSVG } from '@common_components/svg/recurrence.svg';
import ImageOnError from 'components/other/ImageOnError';
import { useTranslation } from 'react-i18next';
interface EventProps {
  event: CalendarEvent;
  title: string;
  allRoleTasks?: Map<string, RoleTaskFull[]>;
  equipmentStack?: Equipment[];
  taskActionImage?: string | null;
  setAllDayOver?: ( id: string | undefined ) => void;
  className?: string;
  children?: JSX.Element;
  usersAbsence?: ShortUser[];
  onContextMenu?: ( open: boolean ) => void;
}

const EventComponent = ( {
  event,
  title,
  allRoleTasks,
  equipmentStack = [],
  taskActionImage,
  setAllDayOver,
  className = '',
  usersAbsence = [],
  children,
} : EventProps ) => {
  const { t } = useTranslation();
  const [ isFirstRender, setIsFirstRender ] = useState( true );

  useEffect( () => {
    setIsFirstRender( false );
  }, [] );

  const setAllDayEventOffset = ( id: string | undefined ) => {
    if ( !isFirstRender && setAllDayOver ) {
      setAllDayOver( id );
    }
  };


  if ( !event.task ) {
    return <></>;
  }

  const isMissingOperators = allRoleTasks && ( TaskUserUtils.isTaskMissingOperators( event.task, allRoleTasks ) > 0 );
  const areEquipmentStack = equipmentStack.length > 0;

  // MULTIPLE DAY INDICATOR ( color bar )
  if ( event.allDayIndicator ) {
    return <div
      data-id={ `task_${ event?.task.id }` }
      data-event={ 'true' }
      className='w-2 h-full'
      onMouseOver={ () => { setAllDayEventOffset( event.task?.id ); } }
      onMouseLeave={ () => { setAllDayEventOffset( undefined ); } }
    />;
  }


  // EVENT ON TOP ( FULLDAY / MULTIPLE DAY )
  if ( event.allDay ) {
    return (
      <div
        className='w-full h-full'
        onMouseOver={ () => { setAllDayEventOffset( event.id ); } }
        onMouseLeave={ () => { setAllDayEventOffset( undefined ); } }
      >
        <div className="flex items-center px-1">
          <div className='flex flex-col w-full'>
            <div className='flex -mb-1 flex-col'>
              <strong className='mr-3'>{title}</strong>
              { !event.fullDay &&
                <div className='flex flex-wrap gap-x-3'>
                  <p>{ dayjs( event.task.dateStart ).format( 'DD/MM hh:mm' ) }</p>
                  <p>{ dayjs( event.task.dateEnd ).format( 'DD/MM hh:mm' ) }</p>
                </div>
              }
            </div>
            <p>{ event.task.location.name }</p>
            <div className='flex flex-wrap end w-fit min-w-16'>
              { isMissingOperators && <ErrorIcons error='understaffed' size={6}/> }
              { areEquipmentStack && <ErrorIcons error='equipment_stack' size={6}/> }
              { usersAbsence.length > 0 && (
                usersAbsence.map( ( user, idx ) => {
                  return <ErrorIcons
                    key={`absence_icon_${user.id}_${idx}`}
                    error='user_missing'
                    size={6}
                    tooltip={ `${user.fullname} ${ t( 'translation:is_absent_on_the_task' ) }` }
                  />;
                } )
              ) }
            </div>
          </div>

          <div className="flex-shrink-0 mx-1">
            <ImageOnError src={typeof taskActionImage === 'string' ? taskActionImage : undefined} className='aspect-square flex w-12' />
          </div>
        </div>

        { children }
      </div>
    );
  }


  // EVENT ON CALENDAR ( USUAL EVENT )
  else {
    return (
      <div
        className={ className + ' w-full h-full p-1' }
        data-id={ `task_${ event?.task.id }` }
        data-event={ 'true' }
      >
        <div className='h-full flex flex-col'>
          <div className="flex flex-wrap items-center mt-1 space-x-2">
            <div className="flex-1">
              <p className='mb-1'><strong>{title}</strong></p>
              <p className='mb-1'>{ dayjs( event.task.dateStart ).format( 'LT' ) } - {dayjs( event.task.dateEnd ).format( 'LT' ) }</p>
              <p className='mb-1'>{ event.task.location.name }</p>
            </div>

            <div className="flex-shrink-0 m-0">
              <div className='flex mt-1'>
                <div style={{ height: `${ ( 10 / 4 ) }rem`,  width: `${ ( 10 / 4 ) }rem`, }}>
                  <ImageOnError src={typeof taskActionImage === 'string' ? taskActionImage : undefined} className='w-full aspect-square' />
                </div>
              </div>
            </div>
          </div>

          <div className='flex flex-wrap m-0 mt-1'>
            { isMissingOperators && <ErrorIcons error='understaffed' size={8}/> }
            { areEquipmentStack && <ErrorIcons error='equipment_stack' size={8}/> }
            { usersAbsence.length > 0 && (
              usersAbsence.map( ( user, idx ) => {
                return <ErrorIcons
                  key={`absence_icon_${user.id}_${idx}`}
                  error='user_missing'
                  size={8}
                  tooltip={ `${user.fullname} ${ t( 'translation:is_absent_on_the_task' ) }` }
                />;
              } )
            ) }
          </div>

          <div className='flex flex-row-reverse h-full w-full'>
            <div className='w-5 h-5 place-self-end self-end'>
              { event.task.idRecurrence != null && <RecurrenceSVG width='1.2rem' height='1.2rem' stroke='dark-gray' /> }
            </div>
          </div>

          { children }

        </div>
      </div>
    );
  }
};

export default EventComponent;